'use client';

import { Box, BoxProps, HeadingProps, Heading, Flex, Text, Spinner, useTokens } from '@mezzoforte/forge';
import { useContext } from 'react';
import { BiddingContext } from '@/context/BiddingContext';
import { SidebarContent, SidebarHeader, SidebarSection } from '@/features/EntryPage/SidebarSection';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { useSession } from '@/hooks/useSession';
import { PriceWithVat } from '@/types/Price';
import { groupBy } from '@/util/collection';
import { formatCurrency } from '@/util/currency';
import { DateTimeFormat } from '@/util/date';
import { HermesEntry } from '@/types/ListEntry';
import { type Bid } from '@/types/bid';

const { fontSizes, fontWeights, lineHeights } = useTokens.huutokaupat;
export interface BidsProps extends BoxProps {
  entryId: number;
  vatPerc: number;
}

const BIDS_MAX_HEIGHT = { base: '180px', md: '230px' };

function Dt(props: BoxProps) {
  return (
    <Box
      as="dt"
      fontSize={fontSizes.bodySmallBold}
      fontWeight={fontWeights.bodySmallBold}
      lineHeight={lineHeights.bodySmallBold}
      {...props}
    />
  );
}
function Dd(props: Partial<HeadingProps>) {
  return (
    <Heading
      variant="h3"
      as="dd"
      {...props}
    />
  );
}

interface BidProps {
  bid: {
    date: Date;
    amount: PriceWithVat;
    isAutomatic: boolean;
    bidder: number;
  };
  isWinning: boolean;
  isOwnBid: boolean;
  isHighestOwnBid: boolean;
  isPriceNegotiation: boolean;
}

function BidComponent({ bid, isWinning, isOwnBid, isHighestOwnBid, isPriceNegotiation }: BidProps) {
  const getBackgroundColor = () => {
    if (isHighestOwnBid && isWinning) {
      return 'backgroundSuccess';
    }

    return isHighestOwnBid ? 'backgroundInfo' : 'background';
  };

  return (
    <Flex
      as="li"
      listStyleType="none"
      backgroundColor={getBackgroundColor()}
      gap={2}
      px={3}
      py={2}
      align="center"
      fontSize="xs"
      _notFirst={{ borderTopWidth: '1px', borderColor: 'lineBorder' }}
      data-test={`bid-row bid-row--${isOwnBid ? 'currentUser' : 'other'}`}
    >
      <Text
        variant="bold"
        fontSize="sm"
        flex="0 0 90px"
        data-test="bid-amount"
      >
        {formatCurrency(bid.amount.amount)}
      </Text>
      <Text
        variant="subdued"
        fontSize="xs"
        lineHeight="short"
        flex="0 0 80px"
      >
        {DateTimeFormat({ day: 'numeric', month: 'numeric' }).format(bid.date)}
        <br />
        klo {DateTimeFormat({ hour: 'numeric', minute: 'numeric', second: '2-digit' }).format(bid.date)}
      </Text>
      <Flex
        flexGrow={1}
        align="center"
        justify="flex-end"
        textAlign="right"
        fontWeight={isHighestOwnBid ? 'semibold' : 'normal'}
        lineHeight="short"
      >
        {isOwnBid && isWinning && <>Sinulla on korkein tarjous</>}
        {isOwnBid && !isWinning && <>Oma tarjous</>}
        {!isOwnBid && bid.isAutomatic && <>Tarjoaja {bid.bidder}, korotus&shy;automaatti</>}
        {!isOwnBid &&
          !bid.isAutomatic &&
          (!isPriceNegotiation ? `Tarjoaja ${bid.bidder}` : <>Tarjoaja {bid.bidder}, hinta&shy;neuvottelu</>)}
      </Flex>
    </Flex>
  );
}

interface Props extends BoxProps {
  entry:
    | HermesEntry
    | {
        auctionEnd: string;
        bidCount: number;
        bidderCount: number;
        bids: Bid[];
        postTradingStartedAt: Date | string | null;
      };
  bidderId: number | null;
  isLoading: boolean;
}

function Bids({ entry, isLoading, bidderId, ...boxProps }: Props) {
  return (
    <SidebarSection
      position="relative"
      {...boxProps}
      aria-busy={isLoading}
      aria-live="polite"
    >
      <SidebarHeader
        as="dl"
        borderBottom="1px solid"
        borderBottomColor="lineBorder"
      >
        <Box>
          <Dt>Tarjouksia</Dt>
          <Dd visibility={isLoading ? 'hidden' : 'visible'}>{entry?.bidCount} kpl</Dd>
        </Box>
        <Box textAlign="right">
          <Dt>Tarjoajia</Dt>
          <Dd visibility={isLoading ? 'hidden' : 'visible'}>{entry?.bidderCount} kpl</Dd>
        </Box>
      </SidebarHeader>
      <SidebarContent
        overflowY="auto"
        height={isLoading ? BIDS_MAX_HEIGHT : 'auto'}
        maxHeight={BIDS_MAX_HEIGHT}
        display={isLoading ? 'flex' : 'block'}
        justifyContent="center"
        alignItems="center"
        p={0}
        translate="no"
      >
        {isLoading && <Spinner />}
        {!isLoading && entry.bidCount === 0 && <SidebarContent>Ei tarjouksia</SidebarContent>}
        {!isLoading && entry.bidCount > 0 && (
          <Box
            as="ul"
            pb={2}
          >
            {entry.bids?.map((bid, index) => (
              <BidComponent
                key={`${bid.date.getTime()}-${bid.amount.amount}`}
                bid={bid}
                isWinning={index === 0}
                isOwnBid={bidderId !== null && bidderId === bid.bidder}
                isHighestOwnBid={
                  bidderId !== null && entry.bids?.filter((b) => b.bidder === bidderId).at(0)?.id === bid.id
                }
                isPriceNegotiation={
                  index === 0 &&
                  entry.postTradingStartedAt !== null &&
                  new Date(entry.bids[0].date) >= new Date(entry.postTradingStartedAt)
                }
              />
            ))}
          </Box>
        )}
      </SidebarContent>
      <Box
        position="absolute"
        bottom={0}
        h={6}
        w="100%"
        background="linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%)"
      />
    </SidebarSection>
  );
}

export default function EntryBids({ entryId, vatPerc, ...boxProps }: BidsProps) {
  const { entryInfo } = useSession();
  const usersEntryInfo = entryInfo(entryId);
  const bidderId = usersEntryInfo.data?.isLoggedIn ? usersEntryInfo.data.bidderId : null;
  const context = useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);

  const loading = context.bids === null && hermesEntry === undefined;

  // Merge API response bid info with real-time data from Hermes
  const entry = hermesEntry ?? {
    auctionEnd: context.auctionEnd.toISOString(),
    bidCount: context.bids?.length ?? 0,
    bidderCount: groupBy(context.bids ?? [], (bid) => bid.bidder).size,
    bids: context.bids ?? [],
    postTradingStartedAt: context.postTradingStartedAt,
  };
  return (
    <Bids
      data-test="bids-list"
      {...boxProps}
      isLoading={loading}
      bidderId={bidderId}
      entry={entry}
    />
  );
}

export function PreviewBids({ ...boxProps }: Omit<BidsProps, 'entryId' | 'vatPerc'>) {
  const context = useContext(BiddingContext);
  const entry = {
    auctionEnd: context.auctionEnd.toISOString(),
    bidCount: context.bids?.length ?? 0,
    bidderCount: groupBy(context.bids ?? [], (bid) => bid.bidder).size,
    bids: context.bids ?? [],
    postTradingStartedAt: context.postTradingStartedAt,
  };
  return (
    <Bids
      data-test="preview-bids-list"
      {...boxProps}
      isLoading={false}
      bidderId={null}
      entry={entry}
    />
  );
}
